import { useMemo, useState } from 'react';
import { Link } from 'react-router';
import { IconChevronDown } from '@tabler/icons-react';
import {
  Badge,
  Button,
  EmptyState,
  InputSearch,
  Menu,
  Metric,
  SegmentedControl,
  cn,
  PRESS,
  formatDate,
  formatMoney,
  formatPercentage,
  type BadgeTone,
} from 'escrowbuild-ui';

import { useT, type AppTranslationKey } from '@/i18n';
import { INVESTORS, PARTICIPATIONS, type Investor } from '@/data/domain';

const STATUS_TONE: Record<Investor['status'], BadgeTone> = {
  active: 'success',
  prospect: 'info',
  closed: 'neutral',
};

const STATUS_LABEL: Record<Investor['status'], AppTranslationKey> = {
  active: 'status.investor.active',
  prospect: 'status.investor.prospect',
  closed: 'status.investor.closed',
};

const KIND_LABEL: Record<Investor['kind'], AppTranslationKey> = {
  fund: 'status.investorKind.fund',
  individual: 'status.investorKind.individual',
  trust: 'status.investorKind.trust',
};

const NO_HOLDING = { projects: 0, committed: 0, contributed: 0 };

/** The filter bar's own caption — `SegmentedControl`'s label is the group's accessible name only. */
const FILTER_LABEL =
  'mb-1.5 block font-display text-2xs font-semibold uppercase tracking-label text-muted';

const ROW_LINK = cn(
  'font-medium text-heading underline decoration-line-strong underline-offset-2',
  'transition-colors duration-[var(--dur-fast)] hover:decoration-brand',
  PRESS,
  'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-focus',
);

/*
   The cell's link is a control of its own at roughly 13px of text, so it takes
   the 44px overlay rather than the tight one: rows are 48px apart, which leaves
   the overlays in adjacent rows no chance to collide.
*/
const CELL_LINK = cn(ROW_LINK, 'relative dhd-target');

const TH = 'bg-surface-alt px-3 py-2 font-display text-2xs font-semibold uppercase tracking-label text-muted';
const TD = 'px-3 py-2 text-body';
/* Money never wraps after the currency sign — `$ 2,610,000.00` broke across two
   lines in the total row, which reads as a different figure. */
const TDNUM = cn(TD, 'text-right font-mono whitespace-nowrap');
const TDFOOT = 'px-3 py-2 text-right font-mono text-sm text-heading whitespace-nowrap';

/**
 * The investor register (§8/§9).
 *
 * Every figure is derived from `PARTICIPATIONS` rather than stored on the
 * investor, so a row here cannot disagree with the cap table of the project it
 * links to — the one comparison a client makes during a walkthrough.
 */
export function InvestorsPage() {
  const t = useT();
  const [kind, setKind] = useState('all');
  const [status, setStatus] = useState('all');
  const [city, setCity] = useState('all');
  const [term, setTerm] = useState('');

  const capital = useMemo(() => {
    const byInvestor = new Map<string, typeof NO_HOLDING>();
    const portfolio = { committed: 0, contributed: 0 };

    for (const participation of PARTICIPATIONS) {
      const entry = byInvestor.get(participation.investorId) ?? { ...NO_HOLDING };
      // A zero-share record is a pipeline entry with no position, so it opens no
      // project and contributes nothing to the totals.
      if (participation.sharePercent > 0) entry.projects += 1;
      entry.committed += participation.committed;
      entry.contributed += participation.contributed;
      byInvestor.set(participation.investorId, entry);
      portfolio.committed += participation.committed;
      portfolio.contributed += participation.contributed;
    }

    return { byInvestor, portfolio };
  }, []);

  const cities = useMemo(
    () => Array.from(new Set(INVESTORS.map((investor) => investor.city))).sort(),
    [],
  );

  const rows = useMemo(() => {
    const needle = term.trim().toLowerCase();
    return INVESTORS.filter((investor) => {
      if (kind !== 'all' && investor.kind !== kind) return false;
      if (status !== 'all' && investor.status !== status) return false;
      if (city !== 'all' && investor.city !== city) return false;
      if (needle === '') return true;
      return [investor.name, investor.email, investor.city, investor.taxId]
        .join(' ')
        .toLowerCase()
        .includes(needle);
    });
  }, [kind, status, city, term]);

  const filtersActive = kind !== 'all' || status !== 'all' || city !== 'all' || term.trim() !== '';

  // The footer totals the rows on screen, not the register, so a filter visibly
  // moves a number instead of only shortening a list.
  const shown = rows.reduce(
    (sum, investor) => {
      const entry = capital.byInvestor.get(investor.id) ?? NO_HOLDING;
      return {
        committed: sum.committed + entry.committed,
        contributed: sum.contributed + entry.contributed,
      };
    },
    { committed: 0, contributed: 0 },
  );

  function clearFilters() {
    setKind('all');
    setStatus('all');
    setCity('all');
    setTerm('');
  }

  const activeCount = INVESTORS.filter((investor) => investor.status === 'active').length;
  const prospectCount = INVESTORS.filter((investor) => investor.status === 'prospect').length;
  const closedCount = INVESTORS.length - activeCount - prospectCount;
  const outstanding = capital.portfolio.committed - capital.portfolio.contributed;

  return (
    <div className="flex flex-col gap-6">
      <div>
        <h1 className="font-display text-3xl font-semibold uppercase tracking-title text-heading">
          {t('investors.title')}
        </h1>
        <p className="mt-1 max-w-3xl text-sm text-muted">{t('investors.intro')}</p>
      </div>

      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <Metric label={t('investors.onRegister')} value={INVESTORS.length} format="number" />
        <Metric
          label={t('status.investor.active')}
          value={activeCount}
          format="number"
          tone="success"
          hint={`${t('investors.prospectCount', { count: prospectCount })} · ${t(
            'investors.closedCount',
            { count: closedCount },
          )}`}
        />
        <Metric
          label={t('project.shell.committed')}
          value={capital.portfolio.committed}
          format="money"
          hint={t('investors.committedHint')}
        />
        <Metric
          label={t('investors.contributedToDate')}
          value={capital.portfolio.contributed}
          format="money"
          tone="info"
          hint={t('investors.outstandingHint', { amount: formatMoney(outstanding) })}
        />
      </div>

      {/* The table draws its own surface rather than sitting in a Panel: a
          panel's padding would inset the header band and the rules that divide
          the rows, which is exactly the edge-to-edge look a data table needs. */}
      <section className="rounded-xs border border-line bg-surface">
        <header className="flex flex-wrap items-start justify-between gap-4 border-b border-line-subtle px-4 py-3">
          <div className="min-w-0">
            <h2 className="font-display text-lg font-semibold uppercase tracking-title text-heading">
              {t('investors.register')}
            </h2>
            <p className="mt-1 text-sm text-muted">{t('investors.registerHint')}</p>
          </div>
        </header>

        <div className="flex flex-col gap-4 border-b border-line-subtle px-4 py-4 lg:flex-row lg:items-end lg:justify-between">
          <div className="flex flex-wrap items-end gap-4">
            <div>
              <span className={FILTER_LABEL}>{t('investors.kind')}</span>
              <SegmentedControl
                label={t('investors.kindFilter')}
                options={[
                  { value: 'all', label: t('common.all') },
                  ...(Object.keys(KIND_LABEL) as Investor['kind'][]).map((value) => ({
                    value,
                    label: t(KIND_LABEL[value]),
                  })),
                ]}
                value={kind}
                onValueChange={setKind}
                size="sm"
              />
            </div>

            <div>
              <span className={FILTER_LABEL}>{t('common.status')}</span>
              <SegmentedControl
                label={t('investors.statusFilter')}
                options={[
                  { value: 'all', label: t('common.all') },
                  ...(Object.keys(STATUS_LABEL) as Investor['status'][]).map((value) => ({
                    value,
                    label: t(STATUS_LABEL[value]),
                  })),
                ]}
                value={status}
                onValueChange={setStatus}
                size="sm"
              />
            </div>

            <div>
              <span className={FILTER_LABEL}>{t('investors.city')}</span>
              <Menu
                label={t('investors.cityFilter')}
                placement="bottom-start"
                items={[
                  { id: 'all', label: t('investors.allCities'), onSelect: () => setCity('all') },
                  ...cities.map((name) => ({ id: name, label: name, onSelect: () => setCity(name) })),
                ]}
                trigger={
                  <Button
                    variant="outline"
                    size="sm"
                    trailingIcon={<IconChevronDown size={14} aria-hidden="true" />}
                  >
                    {city === 'all' ? t('investors.allCities') : city}
                  </Button>
                }
              />
            </div>
          </div>

          <div className="w-full lg:w-72">
            <span className={FILTER_LABEL}>{t('common.search')}</span>
            <InputSearch
              id="investor-register-search"
              ariaLabel={t('investors.searchLabel')}
              placeholder={t('investors.searchPlaceholder')}
              value={term}
              onChange={setTerm}
              onSearch={setTerm}
              size="sm"
            />
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full border-collapse text-sm">
            <caption className="dhd-sr-only">{t('investors.tableCaption')}</caption>
            <thead>
              <tr className="h-12 border-b-2 border-line-strong">
                <th scope="col" className={cn(TH, 'text-left')}>{t('investors.colInvestor')}</th>
                <th scope="col" className={cn(TH, 'text-left')}>{t('investors.kind')}</th>
                <th scope="col" className={cn(TH, 'text-left')}>{t('common.status')}</th>
                <th scope="col" className={cn(TH, 'text-left')}>{t('investors.city')}</th>
                <th scope="col" className={cn(TH, 'text-left')}>{t('investors.colJoined')}</th>
                <th scope="col" className={cn(TH, 'text-right')}>{t('investors.colProjects')}</th>
                <th scope="col" className={cn(TH, 'text-right')}>{t('investors.colCommitted')}</th>
                <th scope="col" className={cn(TH, 'text-right')}>{t('project.shell.contributed')}</th>
                <th scope="col" className={cn(TH, 'text-right')}>{t('investors.colShare')}</th>
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 ? (
                <tr>
                  <td colSpan={9} className="p-0 align-top">
                    <EmptyState
                      variant="table"
                      title={t('investors.emptyTitle')}
                      description={t('investors.emptyDescription')}
                      filtersActive={filtersActive}
                      onClearFilters={clearFilters}
                    />
                  </td>
                </tr>
              ) : (
                rows.map((investor) => {
                  const entry = capital.byInvestor.get(investor.id) ?? NO_HOLDING;
                  const share =
                    capital.portfolio.committed > 0
                      ? (entry.committed / capital.portfolio.committed) * 100
                      : 0;

                  return (
                    <tr key={investor.id} className="h-12 border-b border-line-subtle hover:bg-surface-alt">
                      <th scope="row" className={cn(TD, 'text-left font-normal')}>
                        <Link to={`/investors/${investor.id}`} className={CELL_LINK}>
                          {investor.name}
                        </Link>
                        <span className="mt-0.5 block truncate text-2xs text-muted">
                          {investor.email}
                        </span>
                      </th>
                      <td className={TD}>
                        <Badge tone="neutral" variant="subtle" size="sm">
                          {t(KIND_LABEL[investor.kind])}
                        </Badge>
                      </td>
                      <td className={TD}>
                        <Badge tone={STATUS_TONE[investor.status]} variant="subtle" size="sm">
                          {t(STATUS_LABEL[investor.status])}
                        </Badge>
                      </td>
                      <td className={TD}>{investor.city}</td>
                      <td className={TD}>{formatDate(investor.joinedAt)}</td>
                      <td className={cn(TD, 'text-right')}>{entry.projects}</td>
                      <td className={TDNUM}>{formatMoney(entry.committed)}</td>
                      <td className={TDNUM}>{formatMoney(entry.contributed)}</td>
                      <td className={TDNUM}>
                        {formatPercentage(share, { precision: 1 })}
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
            {rows.length > 0 ? (
              <tfoot>
                <tr className="border-t-2 border-line-strong bg-surface-alt">
                  <th
                    scope="row"
                    colSpan={5}
                    className="px-3 py-2 text-left font-display text-2xs font-semibold uppercase tracking-label text-heading"
                  >
                    <span className="dhd-sr-only">{t('investors.subtotal')} </span>
                    {filtersActive
                      ? t('investors.filteredTotal', { shown: rows.length, total: INVESTORS.length })
                      : t('common.total')}
                  </th>
                  <td className={TDFOOT}>
                    {rows.reduce(
                      (sum, investor) => sum + (capital.byInvestor.get(investor.id) ?? NO_HOLDING).projects,
                      0,
                    )}
                  </td>
                  <td className={TDFOOT}>
                    {formatMoney(shown.committed)}
                  </td>
                  <td className={TDFOOT}>
                    {formatMoney(shown.contributed)}
                  </td>
                  <td className={TDFOOT}>
                    {formatPercentage(
                      capital.portfolio.committed > 0
                        ? (shown.committed / capital.portfolio.committed) * 100
                        : 0,
                      { precision: 1 },
                    )}
                  </td>
                </tr>
              </tfoot>
            ) : null}
          </table>
        </div>
      </section>
    </div>
  );
}
